import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import { nanoid } from "nanoid";
import React, { useRef } from "react";
import {
  Controller,
  FieldValues,
  Validate,
  ValidateResult,
} from "react-hook-form";
import AlertMessage from "../../AlertMessage/AlertMessage";
import { Info } from "../../Info";
import FormLabel from "../FormHelpers/FormLabel";
import styles from "./FormInputWrapper.module.scss";
import { FormInputProps, FormInputWrapperProps } from "./types";

const cx = classNames.bind(styles);

// IMPROVE: probably better for each input to have a bespoke wrapped usage of
// react hook form's Controller rather than trying to centralize it all into
// one. The only functionality that should be shared is the overall input
// structure, with the label and other UI.
/**
 * Adds extra props or updates current props to connect an input to be managed
 * by react hook form.
 *
 * @deprecated use `InputWithLabels` instead
 */
export const FormInputWrapper = ({
  as: Component,
  shouldShowDescriptionInTooltip = true,
  ...props
}: FormInputWrapperProps & FormInputProps) => {
  const {
    name,
    isRequired,
    label,
    validate,
    rules,
    isDisabled,
    description,
    isInline = false,
    defaultValue,
    isOptionalTextShown = true,
    isLabelShown = true,
    isFluid = false,
    componentClassName,
  } = props;
  // strip out style and className and don't pass that through
  const { style, className, ...restProps } = props;
  const labelId = useRef(nanoid(6));
  const inputId = useRef(nanoid(6));

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={{
        ...rules,
        validate: {
          ...(isRequired
            ? {
                isRequired: (value): ValidateResult =>
                  [null, undefined, ""].includes(value)
                    ? `${
                        typeof label === "string" ? label : "Input"
                      } is required`
                    : undefined,
              }
            : {}),
          ...(validate
            ? isPlainObject(validate)
              ? { ...validate }
              : { validate }
            : {}),
        } as Record<string, Validate<any, FieldValues>>,
      }}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { error, isTouched, invalid },
        formState: { isSubmitted },
      }) => {
        // TODO: this state makes no semantic sense and needs to be refactored out
        const isValidated = isTouched || isSubmitted;
        const isValid = !invalid;
        const errorMessage = error?.message;

        // change handler proxy for the underlying form input component
        const handleChange = (...args: any[]) => {
          // pass the changes along to the change handler passed to this wrapper
          props.onChange?.(...args);
          // send changes to react hook form
          return onChange(...args);
        };

        // blur handler proxy for the underlying form input component
        const handleBlur = () => {
          props.onBlur?.();
          onBlur();
        };

        const inputComponent = Component ? (
          <Component
            {...restProps}
            className={componentClassName}
            labelledById={labelId.current}
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            inputRef={ref}
            isValidated={isValidated}
            isValid={isValid}
            error={errorMessage}
            isTouched={isTouched}
            id={inputId.current}
          />
        ) : null;

        return (
          <div
            className={cx("wrapper", { isInline, isFluid }, className)}
            style={style}
          >
            {label && isLabelShown ? (
              <FormLabel
                id={labelId.current}
                inputId={inputId.current}
                label={label}
                isRequired={isRequired}
                isDisabled={isDisabled}
                description={description}
                isOptionalTextShown={isOptionalTextShown}
                shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
              />
            ) : null}

            {description && (!label || (label && !isLabelShown)) ? (
              <div className={cx("inputContainer", { isInline })}>
                {inputComponent}
                <Info content={description} />
              </div>
            ) : (
              inputComponent
            )}

            {!isInline && errorMessage ? (
              <div className={cx("errorMessageContainer")}>
                {!isDisabled && invalid ? (
                  <AlertMessage
                    type="alert"
                    simplified
                  >
                    {errorMessage}
                  </AlertMessage>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
};

export default FormInputWrapper;
